import { useCallback, useEffect, useRef, useState } from "react";

// Per-comparison persistence for expanded row ids. Keyed by (tab, import,
// source) so the user can switch between submittals and materials without
// losing either tab's expansion, and reloads restore state within the same
// session. sessionStorage (not localStorage) because it's tied to the active
// comparison.

function storageKey(
  tab: "submittals" | "materials",
  importLogId?: string,
  sourceLogId?: string
): string | null {
  if (!importLogId || !sourceLogId) return null;
  return `sci-expanded-${tab}:${importLogId}:${sourceLogId}`;
}

function read(key: string | null): string[] {
  if (!key) return [];
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function write(key: string | null, ids: string[]): void {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // quota or privacy mode — fall through; state just won't persist
  }
}

export type UsePersistedExpansion = {
  expandedItemIds: string[];
  setExpandedItemIds: (ids: string[]) => void;
  toggleExpanded: (id: string) => void;
  expandedRef: React.MutableRefObject<string[]>;
};

/**
 * Returns expansion state tied to the current comparison. The `expandedRef`
 * mirror is for handlers that want a stable reference (e.g. AG Grid's
 * onFirstDataRendered where adding state to deps would re-wire the
 * listener on every toggle).
 */
export function usePersistedExpansion(
  tab: "submittals" | "materials",
  importLogId?: string,
  sourceLogId?: string
): UsePersistedExpansion {
  const key = storageKey(tab, importLogId, sourceLogId);
  const [expandedItemIds, setExpandedItemIdsState] = useState<string[]>(() =>
    read(key)
  );
  const expandedRef = useRef<string[]>(expandedItemIds);
  expandedRef.current = expandedItemIds;

  useEffect(() => {
    write(key, expandedItemIds);
  }, [key, expandedItemIds]);

  const setExpandedItemIds = useCallback((ids: string[]) => {
    setExpandedItemIdsState(ids);
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedItemIdsState((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  return { expandedItemIds, setExpandedItemIds, toggleExpanded, expandedRef };
}
